import React, { useState } from 'react';
import useIdleLock from './useIdleLock';
import KitchenPinSignIn from './KitchenPinSignIn';
import { readDeviceToken } from './kitchenDevice';
import { signOutOnServer } from './signOutOnServer';

/**
 * Lock screen for an idle shared device. The page behind it stays mounted (nothing in progress is lost); the
 * person proves who they are with their password again, or signs out. Never a silent redirect.
 *
 * On a registered kitchen device (G-AUTH-2) the lock is the PIN screen instead, starting on whoever was signed in:
 * their PIN starts a fresh session (`onPinSignIn`, App.js completeLogin) and the locked one is signed out. Someone
 * else signing in reloads the app, so nothing of the last person's screen stays behind.
 */
export default function IdleLock({ user, token, apiUrl, onSignOut, minutes, onPinSignIn }) {
  const { locked, unlock } = useIdleLock({ active: !!user, ...(minutes !== undefined ? { minutes } : {}) });
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (!locked) return null;

  if (onPinSignIn && readDeviceToken()) {
    const pinBack = async (data) => {
      const samePerson = data?.user?.id === user?.id;
      await signOutOnServer({ apiUrl, token });      // still the locked session's token: end it
      await onPinSignIn(data);
      unlock();
      if (!samePerson) window.location.assign('/');
    };
    return (
      <div role="dialog" aria-modal="true" aria-label="Screen locked" data-testid="idle-lock"
           style={{ position: 'fixed', inset: 0, zIndex: 100000, background: '#111827', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <KitchenPinSignIn apiUrl={apiUrl} currentUserId={user?.id} onSignedIn={pinBack} onSignOut={onSignOut} />
      </div>
    );
  }

  const submit = async (event) => {
    event.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${apiUrl}/api/auth/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || token}` },
        body: JSON.stringify({ password }),
      });
      if (res.status === 204 || res.ok) {
        setPassword('');
        unlock();
      } else if (res.status === 429) {
        setError('Too many tries. Wait a few minutes, or sign out.');
      } else if (res.status === 401 && (await res.json().catch(() => ({}))).detail !== 'Wrong password') {
        onSignOut(); // the session itself is gone (signed out elsewhere): go to sign-in, not a dead lock screen
      } else {
        setError('That password is not right.');
      }
    } catch {
      setError('Could not reach the server. Check the connection, or sign out.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Screen locked" data-testid="idle-lock"
         style={{ position: 'fixed', inset: 0, zIndex: 100000, background: '#111827', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <form onSubmit={submit} style={{ width: '100%', maxWidth: 360 }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Screen locked</h1>
        <p style={{ opacity: 0.8, marginBottom: 16 }}>
          {user?.name || user?.email || 'This device'} was idle. Enter your password to carry on.
        </p>
        <input type="password" autoFocus autoComplete="current-password" value={password}
               onChange={(e) => setPassword(e.target.value)} aria-label="Password" data-testid="idle-lock-password"
               style={{ width: '100%', padding: 12, borderRadius: 6, color: '#111827', marginBottom: 8 }} />
        {error && <p role="alert" data-testid="idle-lock-error" style={{ color: '#fca5a5', marginBottom: 8 }}>{error}</p>}
        <button type="submit" disabled={busy || !password} data-testid="idle-lock-unlock"
                style={{ width: '100%', padding: 12, borderRadius: 6, background: '#2563eb', color: '#fff', marginBottom: 8 }}>
          {busy ? 'Checking...' : 'Unlock'}
        </button>
        <button type="button" onClick={onSignOut} data-testid="idle-lock-signout"
                style={{ width: '100%', padding: 12, borderRadius: 6, background: 'transparent', color: '#fff',
                         border: '1px solid #6b7280' }}>
          Sign out
        </button>
      </form>
    </div>
  );
}
